import { track } from '@amplitude/marketing-analytics-browser'
import isEmpty from 'lodash.isempty'
import type { PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'
import styled from 'styled-components'

import CalendarPage from 'CalendarPage'
import type {
  AccountSelectorProps,
  ConversationProps,
  UpcomingPostCardProps,
  PostPreviewSectionProps,
  PostActionProps,
} from 'Components'
import {
  Conversation,
  Navbar,
  LoadingPlaceholder,
  PostAction,
  UpcomingPostCard,
  PostPreviewSection,
  EmptyUpcomingPosts,
  NoPostToApprove,
} from 'Components'
import { Title, FlexAlignCenter } from 'Components/CommonStyleComponents'
import type { NavbarTabView, TabViewValue } from 'Components/Navbar'
import InstagramGridPage from 'InstagramGridPage'
import { datePlaceholderForDraftPost } from 'normalizer'

const FlexAlignCenterSticky = styled(FlexAlignCenter)`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fcfcfc;
  width: 102%;
  margin-right: -2%;
  padding-bottom: 10px;
`

const TitleStyled = styled(Title)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0;

  a {
    font-size: 14px;
    color: #4f31ff;
    margin-left: 10px;
    cursor: pointer;
    text-decoration: underline;
  }
`

const Container = styled.div`
  background: #fcfcfc;

  > div {
    display: grid;
    grid-template-columns: 1fr 440px 1fr;
    margin: 0 auto;
    overflow: auto;
    overflow-y: hidden;

    @media (max-width: 1280px) {
      grid-template-columns: 1fr 400px 1fr;
    }
  }
`

const Column = styled.div<{ delayShowColumn?: boolean }>`
  position: relative;
  padding: 10px;

  &:nth-child(1) {
    min-width: 480px;
    background: #fcfcfc;
    padding: 0 30px 0 56px;
    height: calc(100vh - (70px + 24px)); // 24px is sticky header top margin
    overflow-x: auto;
    margin-top: 24px;
    margin-left: auto;

    @media (max-width: 1280px) {
      padding-left: 20px;
      padding-right: 20px;
      min-width: 300px;
      width: 100%;
    }
  }

  &:nth-child(2) {
    border-left: 1px solid #e5e5e5;
    border-right: 1px solid #e5e5e5;
    padding: 24px 40px 0;
    height: calc(100vh - 70px);
    overflow-x: auto !important;
    background: #ffffff;

    @media (max-width: 1280px) {
      padding-left: 20px;
      padding-right: 20px;
    }
  }

  &:last-child {
    padding: 24px 40px 0;
    border-right: none;
    min-width: 480px;
    background: #ffffff;

    .spacer {
      height: 24px;
    }

    @media (max-width: 1280px) {
      min-width: auto;
      padding: 20px;
    }
  }

  ${(props) =>
    props.delayShowColumn &&
    `animation: custom-show-animation 500ms ease-in 0s 0.5;
  `}
`

const UpcomingPostsWrap = styled.div`
  .date {
    font-size: 14px;
    font-weight: 500;
    margin: -6px auto 18px;
    display: flex;
    align-items: center;

    &.hidden {
      display: none;
    }
  }

  .postCardWrap {
    margin-bottom: 18px;
    cursor: pointer;
    scroll-margin: 40px;

    &.hidden {
      display: none;
    }
  }
`

const NoPostToApproveWrap = styled.div`
  margin: 10px 0 16px;
`

const PendingDateWrap = styled.div`
  .bullet {
    color: #4f31ff;
    margin-right: 6px;
    font-size: 18px;
  }
`

interface AppProps {
  lastAction: string | null
  isLoading: boolean
  accountSelectorProps: AccountSelectorProps
  setting: {
    logoUrl: string
    companyName: string
  }
  upcomingPosts: {
    id: string
    date: string
    isVisible: boolean
    posts: (UpcomingPostCardProps & {
      isVisible?: boolean
      postIndex: number
    })[]
  }[]
  postPreviewSectionProps: PostPreviewSectionProps
  conversationProps: ConversationProps
  postActionProps: PostActionProps
  handleCardClick: (postRowId: string, postId: string, status: string) => void
  navigatePost: (postIndex: number) => void
  isReadOnly: boolean
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
}

function App({
  accountSelectorProps,
  lastAction,
  isLoading,
  setting,
  upcomingPosts,
  postPreviewSectionProps,
  conversationProps,
  postActionProps,
  handleCardClick,
  navigatePost,
  isReadOnly,
  updatePostState,
}: PropsWithChildren<AppProps>) {
  const [tabView, setTabView] = useState<TabViewValue>('approval')
  const [shouldShowConfetti, setShouldShowConfetti] = useState(false)

  const renderTotalPendingCount = (posts: AppProps['upcomingPosts']) => {
    const flattenArr = posts.map((item) => item.posts).flat()

    let pendingCount = 0
    flattenArr.forEach((item) => {
      if (item.status === 'pending') {
        pendingCount += 1
      }
    })
    return pendingCount
  }

  const handlePendingCountClick = () => {
    const firstPendingPostIndex = upcomingPosts
      .flatMap((item) => item.posts)
      .findIndex((post) => post.status === 'pending')

    navigatePost(firstPendingPostIndex + 1)
  }

  useEffect(() => {
    if (
      lastAction &&
      renderTotalPendingCount(upcomingPosts) === 0 &&
      ['approved', 'submitChangeDetail'].includes(lastAction)
    ) {
      document.getElementById('post-list-column')?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
      if (!shouldShowConfetti) {
        setShouldShowConfetti(true)

        setTimeout(() => setShouldShowConfetti(false), 5000)
      }
    }
  }, [upcomingPosts, lastAction])

  let navbarTabView: NavbarTabView = {
    defaultValue: tabView,
    leftItem: {
      value: 'approval' as TabViewValue,
      label: 'Approval View',
    },
    middleItem: {
      value: 'calendar' as TabViewValue,
      label: 'Calendar View',
    },
    rightItem: {
      value: 'instagramGrid' as TabViewValue,
      label: 'Instagram Grid',
    },
    onViewChange: (view: TabViewValue) => {
      setTabView(view)

      if (view === 'approval') {
        track('ext-approval-tab-click')
      } else if (view === 'calendar') {
        track('ext-cal-tab-click', {
          isReadOnly,
        })
      } else if (view === 'instagramGrid') {
        track('ext-planner-tab-click', {
          isReadOnly,
        })
      }
    },
  }

  if (isReadOnly) {
    navbarTabView = {
      defaultValue: 'calendar',
      leftItem: {
        value: 'calendar' as TabViewValue,
        label: 'Calendar View',
      },
      rightItem: {
        value: 'instagramGrid' as TabViewValue,
        label: 'Instagram Grid',
      },
      onViewChange: navbarTabView.onViewChange,
    }
  }

  return (
    <div>
      {shouldShowConfetti && <Confetti numberOfPieces={250} recycle={false} />}
      {!isLoading && (
        <Navbar
          logo={setting.logoUrl}
          name={setting.companyName}
          view={navbarTabView}
        />
      )}

      {tabView === 'calendar' ? (
        <CalendarPage setTabView={setTabView} isReadOnly={isReadOnly} />
      ) : tabView === 'instagramGrid' ? (
        <InstagramGridPage
          accountSelectorProps={accountSelectorProps}
          isReadOnly={isReadOnly}
        />
      ) : isLoading ? (
        <LoadingPlaceholder />
      ) : !isLoading &&
        isEmpty(upcomingPosts.flatMap((p) => p.posts)) &&
        !isReadOnly ? (
        <EmptyUpcomingPosts />
      ) : (
        !isReadOnly && (
          <Container>
            <div>
              <Column id="post-list-column">
                {renderTotalPendingCount(upcomingPosts) > 0 && (
                  <FlexAlignCenterSticky>
                    <TitleStyled>
                      <span>Upcoming Posts</span>
                      <a id="pending-count" onClick={handlePendingCountClick}>
                        View Pending ({renderTotalPendingCount(upcomingPosts)})
                      </a>
                    </TitleStyled>
                  </FlexAlignCenterSticky>
                )}

                {renderTotalPendingCount(upcomingPosts) === 0 && (
                  <>
                    <Title>Upcoming Posts</Title>
                    <NoPostToApproveWrap>
                      <NoPostToApprove />
                    </NoPostToApproveWrap>
                  </>
                )}

                <UpcomingPostsWrap id="posts-container">
                  {upcomingPosts.map(
                    ({ date, posts, id: postRowId, isVisible }) => {
                      return (
                        <div key={postRowId}>
                          <div className={`date ${isVisible ? '' : 'hidden'}`}>
                            <PendingDateWrap>
                              {posts.length > 0 && (
                                <>
                                  <span className="bullet">•</span>
                                  {date === datePlaceholderForDraftPost &&
                                    'Drafts'}
                                </>
                              )}
                            </PendingDateWrap>
                            {date !== datePlaceholderForDraftPost && date}
                          </div>
                          {posts.map((post) => (
                            <div
                              className={`postCardWrap ${
                                post.isVisible ? '' : 'hidden'
                              }`}
                              key={post.id}
                              onClick={() =>
                                handleCardClick(postRowId, post.id, post.status)
                              }
                              data-postid={post.id}
                              data-postindex={post.postIndex}
                              data-post-status-pending={
                                post.status === 'pending'
                              }
                            >
                              <UpcomingPostCard
                                {...post}
                                updatePostState={updatePostState}
                              />
                            </div>
                          ))}
                        </div>
                      )
                    }
                  )}
                </UpcomingPostsWrap>
              </Column>
              <Column
                style={{
                  overflowX: 'visible', // for PDF review tooltip cropping issue
                  overflowY: 'auto', // ensure for long post previews the user can scroll the container
                }}
              >
                <PostPreviewSection {...postPreviewSectionProps} />
              </Column>
              <Column delayShowColumn={true} data-testid="post-action-column">
                <PostAction {...postActionProps} />
                <div className="spacer" />
                <Conversation {...conversationProps} />
              </Column>
            </div>
          </Container>
        )
      )}
    </div>
  )
}

export default App
